import React from "react";
import { Link } from "react-router-dom";
import { Users, Receipt, Apple, HeartHandshake, AlertTriangle, UserPlus, RefreshCw, CalendarClock, Package, Baby, ChevronRight, PauseCircle, LayoutDashboard } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { useApiData } from "../../lib/api.js";
import { KartaSeznam, EmptyState, Dlazdice } from "../../components/ui.jsx";
import { NotifikacePrehled } from "../../components/OperationsNotifikace.jsx";
import MesicniPriority from "../../components/MesicniPriority.jsx";


function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function clientHref(name) {
  return `/clients?search=${encodeURIComponent(name || "")}&open=1`;
}
function fmtCz(iso) {
  if (!iso) return "—";
  const p = String(iso).slice(0, 10).split("-");
  return p.length === 3 ? `${Number(p[2])}. ${Number(p[1])}.` : iso;
}
function fmtKc(n) { return (n || 0).toLocaleString("cs-CZ") + " Kč"; }

// 12.9.2026: dlaždice ze vzorníku, stejně jako na manažerském Přehledu.
const TON_DLAZDICE = { violet: "znacka", indigo: "info", sky: "info", emerald: "ok", amber: "pozor", rose: "problem" };
function KpiTile({ icon: Icon, label, value, sub, accent = "violet" }) {
  return (
    <Dlazdice
      popisek={label}
      hodnota={<span className="inline-flex items-center gap-2"><Icon className="h-4 w-4 shrink-0 opacity-70" />{value}</span>}
      ton={TON_DLAZDICE[accent] || "znacka"}
      doplnky={sub != null && sub !== "" ? <span className="text-xs text-slate-500">{sub}</span> : null}
    />
  );
}

// Karta s frontou klientů — dvouřádkové řádky (jméno + kontext), barevný count pill, proklik na detail.
// 12.9.2026: fronta je karta ze vzorníku. Dřív měla barevný rám podle nálady,
// nadpis uvnitř bílé plochy a v řádku text slepený tečkou. Teď je hlavička
// tmavá jako všude, naléhavá fronta má červený odznak a podřádek jsou pilulky.
// `sub` může vrátit pole textů, pak z každého vznikne vlastní pilulka.
function QueueCard({ icon: Icon, iconColor, title, rows, render, sub, dot, accent = "slate", empty = "Nic k řešení" }) {
  const count = rows?.length || 0;
  const naléhavá = accent === "rose" && count > 0;
  return (
    <KartaSeznam ikona={Icon} title={title} odznak={count} odznakTon={naléhavá ? "cervena" : "bila"}>
      {count === 0 ? (
        <EmptyState icon={Icon} title={empty} />
      ) : (
        <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto px-4">
          {rows.map((r, i) => {
            const podradek = sub ? sub(r) : null;
            const pilulky = Array.isArray(podradek) ? podradek.filter(Boolean) : (podradek ? [podradek] : []);
            return (
              <Link key={r.id || i} to={r.id ? clientHref(r.klient) : "#"}
                className="flex items-start gap-2.5 py-2.5 transition-colors hover:bg-slate-50">
                {dot && <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot(r)}`} />}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-slate-800 demo-sensitive">{r.klient}</div>
                  {pilulky.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-1 demo-sensitive">
                      {pilulky.map((x, j) => (
                        <span key={j} className="rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{x}</span>
                      ))}
                    </div>
                  )}
                </div>
                {render && (
                  <span className={`mt-0.5 shrink-0 whitespace-nowrap rounded-lg px-2 py-0.5 text-xs font-bold tabular-nums ${
                    accent === "rose" ? "bg-rose-50 text-rose-700"
                      : accent === "amber" ? "bg-amber-50 text-amber-800"
                      : "bg-slate-100 text-slate-600"
                  }`}>{render(r)}</span>
                )}
                <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-slate-300" />
              </Link>
            );
          })}
        </div>
      )}
    </KartaSeznam>
  );
}

function SectionTitle({ children }) {
  return <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wide">{children}</h2>;
}

export default function CsmDashboard() {
  const monthKey = currentMonthKey();
  const { data } = useApiData("/api/csm-overview");
  const k = data?.kpis || {};
  const q = data?.queues || {};
  const packages = data?.packages || [];
  const trenerOrBalicek = (r) => [r.trener, r.balicek].filter(Boolean);

  return (
    <div className="space-y-5">
      <GradientHeader title="Dashboard" icon={LayoutDashboard}
        subtitle="Klientský servis: retence, platby, výživa a balíčky." />

      <MesicniPriority mesic={monthKey} highlight />

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={Users} accent="violet" label="Aktivní klienti" value={data ? (k.activeClients ?? 0) : "—"} sub="napříč týmem" />
        <KpiTile icon={Receipt} accent="rose" label="Po splatnosti" value={data ? (k.overdueCount ?? 0) : "—"} sub={data ? fmtKc(k.overdueSum) : ""} />
        <KpiTile icon={Apple} accent="sky" label="Konzultace tento měsíc" value={data ? (k.consultationsThisMonth ?? 0) : "—"} sub={data ? `${k.consultationEntitled ?? 0} s nárokem` : ""} />
        <KpiTile icon={Baby} accent="amber" label="Dětské permice" value={data ? (k.childrenPermice ?? 0) : "—"} sub="aktivní" />
      </div>

      {/* Retence */}
      <SectionTitle>Retence</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueCard icon={AlertTriangle} accent="rose" title="Churn riziko (bez tréninku)"
          rows={q.churnRisk} dot={() => "bg-rose-500"} sub={trenerOrBalicek} render={(r) => r.duvod}
          empty="Žádné riziko" />
        <QueueCard icon={PauseCircle} accent="slate" title="Přerušeno"
          rows={q.paused} dot={() => "bg-slate-400"} sub={(r) => r.balicek || "—"}
          render={(r) => (r.konec ? `konec ${fmtCz(r.konec)}` : "bez data")} empty="Žádní přerušení" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueCard icon={UserPlus} accent="emerald" title="Onboarding (noví ≤ 14 dní)"
          rows={q.onboarding} dot={() => "bg-emerald-500"} sub={(r) => r.balicek || "—"}
          render={(r) => `začátek ${fmtCz(r.zacatek)}`} empty="Žádní noví klienti" />
        <QueueCard icon={CalendarClock} accent="amber" title="Konec spolupráce ≤ 30 dní"
          rows={q.endingSoon} dot={() => "bg-amber-400"} sub={trenerOrBalicek}
          render={(r) => `za ${r.dniDoKonce} d, ${fmtCz(r.konec)}`} empty="Nikdo nekončí" />
      </div>
      <QueueCard icon={RefreshCw} accent="violet" title="FLEXIBLE k prodloužení"
        rows={q.flexibleRenewal} dot={(r) => (r.dniDoTerminu < 0 ? "bg-red-500" : "bg-amber-400")} sub={trenerOrBalicek}
        render={(r) => (r.dniDoTerminu < 0 ? `po termínu, ${fmtCz(r.terminProdlouzeni)}` : `do ${fmtCz(r.terminProdlouzeni)}, za ${r.dniDoTerminu} d`)}
        empty="Žádné k prodloužení" />

      {/* Platby */}
      <SectionTitle>Platby</SectionTitle>
      <QueueCard icon={Receipt} accent="rose" title="Faktury po splatnosti"
        rows={q.overdueInvoices} dot={() => "bg-rose-500"} sub={(r) => [`č. ${r.cislo || "—"}`, `splatnost ${fmtCz(r.datumSplatnosti)}`]}
        render={(r) => `${fmtKc(r.castka)}, ${r.dniPoSplatnosti} d`} empty="Vše zaplaceno" />

      {/* Výživa */}
      <SectionTitle>Výživa</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <QueueCard icon={HeartHandshake} accent="violet" title="Nárok na konzultaci"
          rows={q.consultationEntitled} sub={trenerOrBalicek}
          render={(r) => (r.poslKonzultace ? `posl. ${fmtCz(r.poslKonzultace)} (${r.dniOdKonzultace} d)` : "nikdy")}
          empty="Nikdo s nárokem" />
        <QueueCard icon={Apple} accent="sky" title="Konzultace tento měsíc"
          rows={q.consultationsThisMonth} sub={trenerOrBalicek}
          render={(r) => fmtCz(r.poslKonzultace)} empty="Tento měsíc žádné" />
      </div>

      {/* Balíčky */}
      <SectionTitle>Balíčky</SectionTitle>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <Package className="w-5 h-5 text-slate-500" />
          <h3 className="text-base font-bold text-slate-900">Rozpad aktivních klientů</h3>
          <span className="text-sm text-slate-400">(bez dospělých permic)</span>
        </div>
        {packages.length === 0 ? (
          <div className="text-sm text-slate-400 py-2">Žádná data.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
            {packages.map((p) => (
              <div key={p.balicek} className="flex items-center justify-between text-sm border-b border-slate-50 py-1.5">
                <span className="text-slate-700 truncate">{p.balicek}</span>
                <span className="font-bold tabular-nums text-slate-900">{p.pocet}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
          <Baby className="w-4 h-4 text-amber-500" />
          <span className="text-sm text-slate-700 flex-1">Dětské permice</span>
          <span className="font-bold tabular-nums text-amber-700">{data ? (k.childrenPermice ?? 0) : "—"}</span>
        </div>
      </div>

      {/* Notifikace z úkolů (nové, po deadlinu, ke kontrole) — dole dle zadání. */}
      <NotifikacePrehled />
    </div>
  );
}
